import React from "react";
import {
  Box,
  Grid,
} from "@mui/material";
import {
  makeStyles
} from "@mui/styles";
import {
  Link
} from "react-router-dom";
import formatNumber from "../../../../utils/number-formatter";

const NumberMaterials = ({ statistic }) => {
  const validationStats = statistic?.validation_stats;

  return (
    <Grid container spacing="20px">
      <Grid size={4}>
        <CardInfo
          filter="?event_type=no_violation"
          title="Без нарушения ПДД"
          icon={require('../../../../assets/svg/home/icon-no-violation.svg').default}
          count={validationStats?.no_violations?.count}
        />
      </Grid>
      <Grid size={4}>
        <CardInfo
          filter="?event_type=apvgk"
          title="Со скрытыми ГРЗ"
          icon={require('../../../../assets/svg/home/icon-hidden-grz.svg').default}
          count={validationStats?.hidden_plates?.count}
        />
      </Grid>
      <Grid size={4}>
        <CardInfo
          filter="?event_type=violation"
          title="С нарушением ПДД"
          icon={require('../../../../assets/svg/home/icon-violation.svg').default}
          count={validationStats?.violations?.count}
        />
      </Grid>
    </Grid>
  )
}

const VolumeDistributionCard = ({ statistic }) => {
  const classes = useStyles();
  const validationStats = statistic?.validation_stats;

  return (
    <Box className={classes.chartCard}>
      <VolumeDistributionChart validationStats={validationStats} />
    </Box>
  )
}

const CardInfo = ({ title, filter, count, icon }) => {
  const classes = useStyles();
  return (
    <Link to={`/materials${filter}`} className={classes.cardInfo}>
      <img src={icon} width={40} height={40} alt="" />
      <div className={classes.cardInfoTitle}>{title}</div>
      <Box className={classes.cardInfoStats}>
        <span className={classes.cardInfoCount}>{formatNumber(count, {decimals: 0})} шт.</span>
      </Box>
    </Link>
  )
}

const VolumeDistributionChart = ({ validationStats }) => {
  const classes = useStyles();

  const data = [
    {
      label: "Без нарушения ПДД",
      value: validationStats?.no_violations?.count || 0,
      color: "#86EFAC",
    },
    {
      label: "Со скрытыми ГРЗ",
      value: validationStats?.hidden_plates?.count || 0,
      color: "#FDE047",
    },
    {
      label: "С нарушением ПДД",
      value: validationStats?.violations?.count || 0,
      color: "#FCA5A5",
    },
  ].filter(item => item.value > 0);

  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) {
    return (
      <Box className={classes.chartEmpty}>
        Нет данных для отображения графика
      </Box>
    );
  }

  const segments = data.map((item) => {
    const percentage = (item.value / total) * 100;
    return {
      ...item,
      percentage: percentage.toFixed(1),
      width: percentage,
    };
  });

  return (
    <Box>
      <div className={classes.chartTitle}>График распределения объемов</div>
      <div className={classes.chartTotal}>{formatNumber(total, {decimals: 0})} шт.</div>

      <Box className={classes.chartBarContainer}>
        {segments.map((segment, index) => (
          <Box
            key={index}
            className={classes.chartBarSegment}
            sx={{
              width: `${segment.width}%`,
              backgroundColor: segment.color,
              borderRadius: index === 0 ? "4px 0 0 4px" : index === segments.length - 1 ? "0 4px 4px 0" : 0,
            }}
          />
        ))}
      </Box>

      <Box className={classes.legendContainer}>
        {segments.map((segment, index) => (
          <Box key={index} className={classes.legendRow}>
            <Box className={classes.legendLeft}>
              <Box className={classes.legendDot} sx={{ backgroundColor: segment.color }} />
              <span className={classes.legendLabel}>{segment.label}</span>
            </Box>
            <Box className={classes.legendRight}>
              <span className={classes.legendValue}>{formatNumber(segment.value, {decimals: 0})} шт.</span>
              <span className={classes.legendBadge}>{segment.percentage}%</span>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles(() => ({
  cardInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    padding: 16,
    borderRadius: 8,
    background: "#FFFFFF",
    boxSizing: "border-box",
    border: "1px solid #E4E4E7",
    textDecoration: "none !important",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0px 4px 8px -4px rgba(22, 34, 51, 0.08)",
    }
  },
  cardInfoTitle: {
    fontSize: 16,
    color: "#000000",
    fontWeight: 400,
    lineHeight: "1",
  },
  cardInfoStats: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardInfoCount: {
    fontSize: 24,
    fontWeight: 500,
    lineHeight: "28px",
    color: "#000000",
  },
  chartCard: {
    padding: 16,
    borderRadius: 8,
    background: "#FFFFFF",
    border: "1px solid #E4E4E7",
  },
  chartTitle: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: "28px",
    color: "#111111",
    marginBottom: 4,
  },
  chartTotal: {
    fontSize: 24,
    fontWeight: 500,
    lineHeight: "28px",
    color: "#4F39F6",
    marginBottom: 20,
  },
  chartEmpty: {
    fontSize: 14,
    color: "#71717A",
    textAlign: "center",
    padding: "20px 0",
  },

  chartBarContainer: {
    display: "flex",
    height: 12,
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 20,
  },
  chartBarSegment: {
    height: "100%",
  },

  legendContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  legendRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  legendLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: "100%",
  },
  legendLabel: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "18px",
    color: "#000000",
  },
  legendRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  legendValue: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "18px",
    color: "#71717A",
  },
  legendBadge: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "18px",
    color: "#432DD7",
    backgroundColor: "#E0E7FF",
    borderRadius: 16,
    padding: "2px 8px",
  },
}));

export default NumberMaterials
export { VolumeDistributionCard }
